import { ChangeDetectionStrategy, Component, OnInit, forwardRef } from '@angular/core';
import { FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { extractErrors } from '@vsolv/dev-kit/ngx';
import { Property } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { map, takeUntil } from 'rxjs';
import { createPropertyControl } from '../properties';
import { StaticPropertyFormComponent } from '../static-property-form.component';
import {
  buildHinPatch,
  getMarineVesselFieldRole,
  HinDecoder,
  hinValidator,
  MarineVesselFieldKeys,
  MarineVesselFieldRole,
} from './hin-decode';

type MarineVesselAssignment = Property.ObjectPropertyAssignment & {
  property: Property.Model<Property.PropertyType>;
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-marine-vessel-property-input',
  templateUrl: './marine-vessel-property-input.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MarineVesselPropertyInputComponent),
    },
    { provide: NG_VALIDATORS, useExisting: MarineVesselPropertyInputComponent, multi: true },
  ],
})
export class MarineVesselPropertyInputComponent extends StaticPropertyFormComponent implements OnInit {
  constructor(private toastSvc: ToastService) {
    super();
  }

  override property!: Property.ObjectModel;

  assignments: MarineVesselAssignment[] = [];
  visibleExpandedAssignments: MarineVesselAssignment[] = [];
  fieldRoles: Partial<Record<MarineVesselFieldRole, MarineVesselAssignment>> = {};

  form = new FormGroup<Record<string, FormControl>>({});
  expanded = false;

  formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));

  private hinDecoder = new HinDecoder(this.toastSvc);

  get hinAssignment(): MarineVesselAssignment | undefined {
    return this.fieldRoles.hin;
  }

  get hinControl(): FormControl | null {
    const assignment = this.hinAssignment;
    if (!assignment) return null;

    return this.form.get(assignment.property.valueKey) as FormControl | null;
  }

  getSummaryValue(role: Exclude<MarineVesselFieldRole, 'hin'>): string | number | null {
    const assignment = this.fieldRoles[role];
    if (!assignment) return null;

    return this.form.get(assignment.property.valueKey)?.value ?? null;
  }

  override writeValue(value: unknown): void {
    super.writeValue(value);

    if (value && typeof value === 'object') {
      this.form.patchValue(value as Partial<typeof this.form.value>, { emitEvent: false });
      this.syncValidityAfterValueLoad();
    }
  }

  /**
   * Nested required children live on `this.form`. The outer `formControl` only requires the
   * object itself, so incomplete existing vessels would otherwise still look valid.
   */
  override validate(): ValidationErrors | null {
    if (this.form) {
      const formErrors = extractErrors(this.form);
      if (formErrors) {
        this.valid = false;
        return formErrors;
      }
    }
    return super.validate();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.assignments = this.getAssignments();
    this.fieldRoles = this.resolveFieldRoles(this.assignments);
    this.visibleExpandedAssignments = this.assignments.filter(assignment => !this.isHinAssignment(assignment));
    this.form = this.buildForm(this.assignments);
    this.formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));

    const existingValue = this.formControl?.value;
    if (existingValue && typeof existingValue === 'object') {
      this.form.patchValue(existingValue as Record<string, unknown>, { emitEvent: false });
    }

    this.syncValidityAfterValueLoad();

    this.hinControl?.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(rawHin => {
      this.tryDecodeHin(String(rawHin ?? ''));
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.formControl.setValue(this.form.value);
    });
  }

  private syncValidityAfterValueLoad() {
    if (!this.form || this.form.valid) return;

    this.expanded = true;
    this.form.markAllAsTouched();
    this.formControl.updateValueAndValidity({ emitEvent: false });
  }

  toggle() {
    this.expanded = !this.expanded;
  }

  private getAssignments(): MarineVesselAssignment[] {
    return (this.property.properties ?? [])
      .filter((assignment: Property.ObjectPropertyAssignment): assignment is MarineVesselAssignment =>
        Boolean(assignment.property)
      )
      .sort((a: MarineVesselAssignment, b: MarineVesselAssignment) => a.order - b.order);
  }

  private buildForm(assignments: MarineVesselAssignment[]): FormGroup<Record<string, FormControl>> {
    const controls = assignments.reduce<Record<string, FormControl>>((current, assignment) => {
      const control = createPropertyControl(assignment.property, assignment.required);

      if (this.fieldRoles.modelYear?.property.valueKey === assignment.property.valueKey) {
        control.addValidators([Validators.min(1000), Validators.max(9999)]);
      }

      if (this.fieldRoles.hin?.property.valueKey === assignment.property.valueKey) {
        control.addValidators(hinValidator());
      }

      current[assignment.property.valueKey] = control;
      return current;
    }, {});

    return new FormGroup(controls);
  }

  private resolveFieldRoles(
    assignments: MarineVesselAssignment[]
  ): Partial<Record<MarineVesselFieldRole, MarineVesselAssignment>> {
    return assignments.reduce<Partial<Record<MarineVesselFieldRole, MarineVesselAssignment>>>((roles, assignment) => {
      const role = this.resolveAssignmentRole(assignment);
      if (role && !roles[role]) roles[role] = assignment;

      return roles;
    }, {});
  }

  private resolveAssignmentRole(assignment: MarineVesselAssignment): MarineVesselFieldRole | null {
    return getMarineVesselFieldRole([assignment.displayLabel, assignment.property.name, assignment.property.valueKey]);
  }

  private isHinAssignment(assignment: MarineVesselAssignment): boolean {
    return this.fieldRoles.hin?.property.valueKey === assignment.property.valueKey;
  }

  private async tryDecodeHin(rawHin: string) {
    const result = await this.hinDecoder.decode(rawHin);
    if (!result) return;

    const keys = Object.fromEntries(
      Object.entries(this.fieldRoles).map(([role, assignment]) => [role, assignment?.property.valueKey])
    ) as MarineVesselFieldKeys;
    this.form.patchValue(buildHinPatch(keys, result.decoded, result.hin));
  }
}
